import { forwardRef, useRef, useEffect, useImperativeHandle } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';

import { CheckedIcon, IndeterminateIcon } from './CheckboxIcons';
import { ACTIVE_INPUT_OPACITY } from '@utils/constants';

export interface CheckboxInputProps {
  /**
   * The unique ID (attribute) of the html input element.
   */
  id?: string;
  /**
   * Name (attribute) of the html input element.
   */
  name?: string;
  /**
   * Value of the html input element.
   */
  value?: string;
  /**
   * Specifies if the input is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies if the input should be checked by default.
   */
  isChecked?: boolean;
  /**
   * Boolean that specifies if the input indeterminate. If set to true, we'll set the indeterminate attribute to the input
   * and display the checkbox with a minus icon to indicate the indeterminate state.
   */
  isIndeterminate?: boolean;
  /**
   * Specifies if the input is invalid. When set to true, we'll render the checkbox in invalid state.
   */
  isInvalid?: boolean;
  /**
   * Specifies if the input is required. When set to true, we'll set the required attribute to the input element.
   */
  isRequired?: boolean;
  /**
   * Styles overrides.
   */
  className?: string;
  /**
   * Checkbox change handler. Called when the checkbox state changes.
   */
  onChange?: () => void;
}

const Wrapper = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{ isDisabled: boolean }>`
  display: inline-flex;
  position: relative;
  top: 1px;
`;

const Input = styled.input`
  opacity: 0;
  cursor: inherit;
  position: absolute;
`;

const Control = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isInvalid',
})<{
  isInvalid: boolean;
}>`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${tokens.radius.m};
  margin-right: ${tokens.spacer.x1};
  border: 1px solid ${tokens.color.ui_border};
  background-color: ${tokens.color.ui_background};
  position: relative;
  ${({ isInvalid }) =>
    isInvalid && `border-color: ${tokens.color.ui_border_danger};`}

  svg {
    position: absolute;
  }

  ${Input}:checked + &,
  ${Input}:indeterminate + & {
    background-color: ${tokens.color.ui_active};
    border-color: ${({ isInvalid }) =>
      isInvalid
        ? tokens.color.ui_border_danger
        : tokens.color.ui_border_active};
  }

  ${Input}:hover:not(:checked):not(:disabled) + & {
    border: 1px solid ${tokens.color.ui_border_alt};
  }

  ${Input}:focus + & {
    ${tokens.focus()};
  }

  ${Input}:disabled + & {
    background-color: ${tokens.color.ui_background};
    border-color: ${tokens.color.ui_disabled};
  }

  ${Input}:disabled:checked + &,
  ${Input}:disabled:indeterminate + & {
    background-color: ${tokens.color.ui_active};
    border-color: ${tokens.color.ui_border_active};
    opacity: ${ACTIVE_INPUT_OPACITY};
  }
`;

export const CheckboxInput = forwardRef<HTMLInputElement, CheckboxInputProps>(
  (
    {
      id,
      name,
      value,
      isDisabled = false,
      isChecked = false,
      isIndeterminate = false,
      isInvalid = false,
      isRequired = false,
      className,
      onChange,
    }: CheckboxInputProps,
    ref,
  ) => {
    const inputRef = useRef();

    const handleChange = () => {
      onChange && onChange();
    };

    useImperativeHandle(ref, () => inputRef.current);

    useEffect(() => {
      if (inputRef && inputRef.current) {
        (inputRef.current as HTMLInputElement).indeterminate = isIndeterminate;
      }
    }, [isIndeterminate, inputRef]);

    return (
      <Wrapper className={className} isDisabled={isDisabled}>
        <Input
          type="checkbox"
          id={id}
          name={name}
          value={value}
          ref={inputRef}
          checked={isChecked}
          disabled={isDisabled}
          required={isRequired}
          onChange={handleChange}
        />
        <Control isInvalid={isInvalid}>
          {isChecked ? <CheckedIcon /> : null}
          {!isChecked && isIndeterminate ? <IndeterminateIcon /> : null}
        </Control>
      </Wrapper>
    );
  },
);

CheckboxInput.displayName = 'CheckboxInput';
